import { useEffect, useState } from 'react';

import { useClientStorageFlag } from '@/entities/clientStorage';
import { useCurrentUser } from '@/entities/user/currentUser';

import { IS_WHITE_LABEL } from '@/shared/environments';
import { isAnyModalOpen, subscribeToSettledModalClose } from '@/shared/lib/modal';

import { ACQUISITION_SURVEY_STORAGE_KEY, SUBSCRIPTION_REFRESH_DELAYS_MS } from '../lib/constants';
import { getAcquisitionSurveyGate, isNewSubscription } from '../lib/getAcquisitionSurveyGate';
import {
  acquisitionSurveyRequestOccupant,
  useAcquisitionSurveyRequestStore,
  useAcquisitionSurveyStore,
} from './useAcquisitionSurveyStore';

export const useAcquisitionSurveyTrigger = () => {
  const isRequested = useAcquisitionSurveyRequestStore((state) => state.isRequested);
  const clearRequest = useAcquisitionSurveyRequestStore((state) => state.clear);
  const openSurvey = useAcquisitionSurveyStore((state) => state.open);
  const { data: subscription, refetch: refetchUser } = useCurrentUser((user) => user.subscription);
  const [modalCloseCount, setModalCloseCount] = useState(0);

  const isCandidate = isRequested && !IS_WHITE_LABEL && Boolean(subscription && isNewSubscription(subscription, Date.now()));
  const { hasShown, isError, isLoading, markAsShown } = useClientStorageFlag(ACQUISITION_SURVEY_STORAGE_KEY, {
    enabled: isCandidate,
  });
  const isWaitingForSubscription = isRequested && !IS_WHITE_LABEL && !subscription;

  useEffect(() => {
    if (!isWaitingForSubscription) return;
    let isActive = true;
    let timeoutId: ReturnType<typeof setTimeout> | undefined;
    const refetchAfter = ([delay, ...laterDelays]: number[]) => {
      timeoutId = setTimeout(() => {
        void refetchUser().then(({ data: refreshedSubscription }) => {
          if (!isActive || refreshedSubscription) return;
          if (laterDelays.length > 0) refetchAfter(laterDelays);
          else clearRequest();
        });
      }, delay);
    };
    refetchAfter(SUBSCRIPTION_REFRESH_DELAYS_MS);
    return () => {
      isActive = false;
      clearTimeout(timeoutId);
    };
  }, [clearRequest, isWaitingForSubscription, refetchUser]);

  useEffect(() => {
    if (!isRequested) return;

    const gate = IS_WHITE_LABEL
      ? 'drop'
      : getAcquisitionSurveyGate({
          hasShown,
          isFlagError: isError,
          isFlagLoading: isLoading,
          now: Date.now(),
          subscription,
        });

    if (gate === 'wait') return;

    if (gate === 'drop') {
      clearRequest();
      return;
    }

    if (isAnyModalOpen(acquisitionSurveyRequestOccupant)) {
      return subscribeToSettledModalClose(() => setModalCloseCount((count) => count + 1));
    }

    openSurvey();
    markAsShown();
    clearRequest();
  }, [clearRequest, hasShown, isError, isLoading, isRequested, markAsShown, modalCloseCount, openSurvey, subscription]);
};
